import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual, isFunction } from 'lodash-es';
import { usePreviousValue } from '@frontend/design-system';
import { CompactTableData, RowSelectionConfig } from '../types';

export const useSelectableRows = <T extends CompactTableData>({
  data,
  rowSelectionConfig,
}: {
  data: T[];
  rowSelectionConfig: RowSelectionConfig<T> | undefined;
}) => {
  const [totalSelectedRows, setSelectedRows] = useState<T[]>([]);
  const { isRowDisabled, onSelectionToggle } = rowSelectionConfig || {};
  const selectableRows = useMemo(
    () => (isFunction(onSelectionToggle) ? data.filter((item) => !isRowDisabled?.(item)) : []),
    [data, isRowDisabled]
  );
  const selectedRows = useMemo(
    () => totalSelectedRows.filter((row) => selectableRows.some((selectableRow) => selectableRow.id === row.id)),
    [totalSelectedRows, selectableRows]
  );

  const areAllRowsSelected = useMemo(
    () => selectableRows.length > 0 && selectedRows.length === selectableRows.length,
    [selectableRows, selectedRows]
  );

  const selectAllRows = useCallback(() => {
    setSelectedRows(selectableRows);
  }, [selectableRows, setSelectedRows]);

  const deselectAllRows = useCallback(() => setSelectedRows([]), [setSelectedRows]);
  const toggleSelectAllRows = useCallback(() => {
    if (areAllRowsSelected) {
      deselectAllRows();
    } else {
      selectAllRows();
    }
  }, [areAllRowsSelected, deselectAllRows, selectAllRows]);

  const [prevSelectedRows, currentSelectedRows] = usePreviousValue(selectedRows);
  useEffect(() => {
    if (!isEqual(prevSelectedRows, currentSelectedRows)) {
      onSelectionToggle?.(currentSelectedRows);
    }
  }, [prevSelectedRows, currentSelectedRows]);

  const handleRowSelect = useCallback(
    (item: T, isSelected: boolean) => {
      setSelectedRows((prevRows) => {
        if (isSelected) {
          return prevRows?.filter((row) => row.id !== item.id);
        }
        return [...(prevRows || []), item];
      });
    },
    [setSelectedRows]
  );

  return { selectedRows, setSelectedRows, selectableRows, areAllRowsSelected, toggleSelectAllRows, handleRowSelect };
};
